import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Ability, subject } from '@casl/ability';
import { ProjectsService } from '../../../../../../core/projects.service';
import { projectDetails } from '../../../../../../core/projects.selectors';
import { appDataDetails } from '../../../../../../../../shared/core/shared.selectors';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components';
import { MgvwRecordsService } from '../mgvw-records.service';
import { CreateOrEditMgvwRecordComponent } from '../create-or-edit-mgvw-record/create-or-edit-mgvw-record.component';
import { MGVW_STATUS_MAP, isMgvwReadOnly, mgvwStatusDate, mgvwStatusKey } from '../mgvw-form.util';

/**
 * MGVW records list (project-scoped). One row per truck on the contract,
 * newest first, exactly the set the mobile MGVW tab shows: the api returns
 * the whole list (no server paging), so search and paging are client-side.
 * Row click opens the record in the side panel; the header offers the same
 * actions as mobile's tab header (+ create, Export XLS).
 */
@Component({
  selector: 'app-mgvw-records-list',
  templateUrl: './mgvw-records-list.component.html',
  styleUrls: ['./mgvw-records-list.component.scss'],
  standalone: false,
})
export class MgvwRecordsListComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = [
    'truckId',
    'truckLicense',
    'vendor',
    'configuration',
    'allowableLegal',
    'status',
    'actions',
  ];
  dataSource: any[] = [];
  allRecords: any[] = [];
  filteredRecords: any[] = [];
  selectedRow: any;
  projectId: string;
  projectName: string;
  canCreate = false;
  canDelete = false;
  loading = false;
  exporting = false;
  loadError: string | null = null;

  searchForm = new FormGroup({
    searchText: new FormControl(''),
  });
  /** Same chip vocabulary the RFI / change-order lists use. */
  statusMap = MGVW_STATUS_MAP;

  totalRecords = 0;
  currentPage = 1;
  itemsPerPage = 10;

  /** `values.vendor` is the contractor's AASHTOWare id; names come off the project. */
  private vendorNames: Record<string, string> = {};
  /** `?mgvwId=` deep link (what rowClicked writes) auto-opens that record once. */
  private pendingDeepLinkId: string | null = null;
  private readonly onDestroy = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private dialog: MatDialog,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private mgvwRecordsService: MgvwRecordsService,
    private ability: Ability,
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
      this.refreshAbilities();
    }

    // /v1/appdata replaces the ability rules after load (the web session's
    // rules differ from the login payload under org web read-only mode), so
    // re-evaluate once the fresh set is in.
    this.store
      .select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.refreshAbilities());

    this.pendingDeepLinkId = this.route.snapshot.queryParamMap.get('mgvwId');

    this.fetchRecords();

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => (this.selectedRow = null));

    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((project: any) => {
        if (!project) return;
        this.projectName = project?.name || project?.number || '';
        const names: Record<string, string> = {};
        (project?.sub_contractors || []).forEach((sc: any) => {
          if (sc?.source_entity_id !== null && sc?.source_entity_id !== undefined) {
            names[String(sc.source_entity_id)] = sc?.name || sc?.company_name || '';
          }
        });
        this.vendorNames = names;
      });

    this.searchForm.get('searchText').valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(() => this.applySearch(true));

    // Refresh after create/update/submit/delete from the side panel.
    this.mgvwRecordsService.refresh
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.fetchRecords());
  }

  private refreshAbilities(): void {
    if (!this.projectId) return;
    this.canCreate = this.ability.can('create', subject('MgvwRecord', { project_id: this.projectId }));
    this.canDelete = this.ability.can('delete', subject('MgvwRecord', { project_id: this.projectId }));
  }

  fetchRecords(): void {
    if (!this.projectId) return;
    this.loading = true;
    this.loadError = null;
    this.mgvwRecordsService
      .list(this.projectId)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          const list = Array.isArray(resp?.data) ? resp.data : [];
          this.allRecords = list;

          // Each response rebuilds the row objects, so re-point the reference
          // the highlighted-row styling compares against.
          if (this.selectedRow?.uuid) {
            const current = list.find(row => row?.uuid === this.selectedRow.uuid);
            if (current) this.selectedRow = current;
          }

          this.applySearch(true);
          this.loading = false;
          this.tryOpenDeepLinkedRecord();
        },
        error: err => {
          this.loading = false;
          this.loadError = err?.error?.message || err?.message || 'Failed to load MGVW records';
          this.allRecords = [];
          this.filteredRecords = [];
          this.dataSource = [];
          this.totalRecords = 0;
        },
      });
  }

  /** Same four fields the mobile tab's search box matches on. */
  private applySearch(resetPage = false): void {
    const q = String(this.searchForm.get('searchText').value || '').trim().toLowerCase();
    this.filteredRecords = !q
      ? this.allRecords
      : this.allRecords.filter(row => {
        const v = row?.values || {};
        return [v.truck_id, v.truck_license, v.trailer_license, v.truck_description, this.vendorName(row)]
          .some(field => String(field || '').toLowerCase().includes(q));
      });
    this.totalRecords = this.filteredRecords.length;
    this.paginateData(resetPage);
  }

  private tryOpenDeepLinkedRecord(): void {
    if (!this.pendingDeepLinkId) return;
    const id = this.pendingDeepLinkId;
    this.pendingDeepLinkId = null;
    const match = this.allRecords.find(row => `${row?.uuid}` === `${id}` || `${row?.id}` === `${id}`);
    if (!match) return;
    this.rowClicked(match);
    const index = this.filteredRecords.indexOf(match);
    if (index >= 0) {
      this.currentPage = Math.floor(index / this.itemsPerPage) + 1;
      this.paginateData();
    }
  }

  vendorName(row: any): string {
    const id = row?.values?.vendor;
    if (id === null || id === undefined || id === '') return '';
    return this.vendorNames[String(id)] || String(id);
  }

  statusKey(row: any): string {
    return mgvwStatusKey(row);
  }

  clearSearch(): void {
    this.searchForm.reset({ searchText: '' });
  }

  statusDate(row: any): string {
    return mgvwStatusDate(row);
  }

  isFromAashtoware(row: any): boolean {
    return row?.origin === 'aashtoware';
  }

  canDeleteRow(row: any): boolean {
    return this.canDelete && !isMgvwReadOnly(row);
  }

  rowClicked(row: any): void {
    this.selectedRow = row;
    this.projectsService.openRightPanel.next({
      component: CreateOrEditMgvwRecordComponent,
      data: { ...row },
    });
    this.router.navigate([], {
      queryParams: { mgvwId: row.uuid },
      queryParamsHandling: 'merge',
    });
  }

  create(): void {
    this.selectedRow = null;
    this.router.navigate([], {
      queryParams: { mgvwId: null },
      queryParamsHandling: 'merge',
    });
    this.projectsService.openRightPanel.next({
      component: CreateOrEditMgvwRecordComponent,
      data: {},
    });
  }

  viewPdf(row: any, event?: Event): void {
    event?.stopPropagation();
    this.mgvwRecordsService.downloadPdf(row.uuid).catch(err => {
      this.commonService.notification(err?.message || 'Could not download the PDF', 'error');
    });
  }

  exportXls(): void {
    if (this.exporting || !this.projectId) return;
    this.exporting = true;
    this.mgvwRecordsService
      .downloadXls(this.projectId, this.projectName)
      .catch(err => this.commonService.notification(err?.message || 'Could not export the report', 'error'))
      .finally(() => (this.exporting = false));
  }

  deleteRecord(row: any, event?: Event): void {
    event?.stopPropagation();
    const truck = row?.values?.truck_id || 'this record';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete MGVW record',
        `Delete the MGVW record for <b>${truck}</b>? This cannot be undone.`,
        'Delete',
        'Cancel',
      ),
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.mgvwRecordsService
        .delete(row.uuid)
        .pipe(takeUntil(this.onDestroy))
        .subscribe({
          next: () => {
            if (this.selectedRow?.uuid === row.uuid) {
              this.projectsService.closeRightPanel.next();
            }
            this.commonService.notification('MGVW record deleted', 'success');
            this.fetchRecords();
          },
          error: err => {
            this.commonService.notification(err?.error?.message || err?.message || 'Delete failed', 'error');
          },
        });
    });
  }

  onPageChanged(page: number): void {
    this.projectsService.closeRightPanel.next();
    this.currentPage = page;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) this.currentPage = 1;
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredRecords.length);
    this.dataSource = this.filteredRecords.slice(startIndex, endIndex);
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
